import { useMemo } from "react";
import { useGetTreeDataQuery } from "../../../../services/dashboardEndpoints";
import { useAppSelector } from "../../../../../../hooks/storeHooks";
import { selectSearch } from "../../../../layoutSlice";
import type { EntityType, TreeItemData, TreeItemDataOut } from "../../../../services/dashboardTypes";

function generatePathsWithDerivedStatus(items: TreeItemData[], parentPath: string = "/customers"): TreeItemDataOut[] {
  return items?.map((item) => {
    const currentPath = generateCurrentPath(item, parentPath);
    const childrenWithPaths = item.children ? generatePathsWithDerivedStatus(item.children, currentPath) : undefined;
    let itemType: EntityType | "resource";
    if (parentPath === "/customers") {
      itemType = "customer";
    } else if (parentPath.includes("/customers") && !parentPath.includes("/sites")) {
      itemType = "site";
    } else if (parentPath.includes("/sites") && !parentPath.includes("/locations")) {
      itemType = "location";
    } else if (parentPath.includes("/locations") && !parentPath.includes("/meters")) {
      itemType = "meter";
    } else {
      itemType = "resource";
    }
    const entityType = itemType != "resource" ? itemType : null;
    const entityId = itemType != "resource" ? item.id : null;
    const status = deriveStatus(item, childrenWithPaths);

    return {
      ...item,
      id: `${itemType}-${item.id}`,
      entityType,
      entityId,
      status,
      path: currentPath,
      children: childrenWithPaths,
    };
  });
}

function generateCurrentPath(item: TreeItemData, parentPath: string): string {
  if (parentPath === "/customers") {
    return `${parentPath}/${item.id}`;
  }
  if (parentPath.includes("/customers") && !parentPath.includes("/sites")) {
    return `${parentPath}/sites/${item.id}`;
  }
  if (parentPath.includes("/sites") && !parentPath.includes("/locations")) {
    return `${parentPath}/locations/${item.id}`;
  }
  if (parentPath.includes("/locations") && !parentPath.includes("/meters")) {
    return `${parentPath}/meters/${item.id}`;
  }
  return `${parentPath}/${item.id}`;
}

function deriveStatus(
  item: TreeItemData,
  childrenWithPaths?: TreeItemDataOut[],
): {
  issue: string;
  id: string;
  path: string;
}[] {
  if (childrenWithPaths) {
    const childStatuses = childrenWithPaths.flatMap((child) =>
      child.status && child.status.length > 0
        ? child.status.map(() => ({
            issue: `issue with ${child.name}`,
            id: child.id,
            path: child.path,
          }))
        : [],
    );
    return childStatuses.filter(
      (value, index, self) => index === self.findIndex((t) => t.id === value.id && t.issue === value.issue),
    );
  }
  return item.status === "Inactive" ? [{ issue: "Inactive", id: item.id.toString(), path: "" }] : [];
}

// Custom hook to use tree data with paths and derived status
export const useTreeDataWithPaths = () => {
  const search = useAppSelector(selectSearch);
  const { data } = useGetTreeDataQuery({ search });

  return useMemo(() => (data ? generatePathsWithDerivedStatus(data.results) : []), [data]);
};
